// submission store holds the request queue and local forage config and retry logic for each submission service
import { usePlanStore } from './plan'
import type Plan from '@/classes/Plan'
import { EverplansStatusMapper } from '@/classes/SubmissionStatuses/EverplansStatusMapper'
import { InsuranceStatusMapper } from '@/classes/SubmissionStatuses/InsuranceStatusMapper'
import { OapStatusMapper } from '@/classes/SubmissionStatuses/OapStatusMapper'
import { isSubmissionError } from '@/classes/SubmissionStatuses/SubmissionStatusMapper'
import type { SubmissionStatusGroup } from '@/classes/SubmissionStatuses/types'
import type {
	EverplansSubmissionStatus,
	OapSubmissionStatus,
	RemoteSubmissionStatus,
} from '@/classes/SubmissionStatuses/types'
import { PlanPacketStatus } from '@/enums/PlanPacketStatus'
import { useCloudSyncService } from '@/services/CloudSync/cloudSyncService'
import { emailReceiptService } from '@/services/emailReceiptService'
import { everplansService } from '@/services/everplansService'
import { inmanService } from '@/services/inmanService'
import { nglService } from '@/services/nglService'
import { sepioService } from '@/services/sepioService'
import { useSubmissionStatusService } from '@/services/submissionStatusService'
import type { SubmissionChannel, SubmissionService } from '@/services/types'
import type { PlanBasicData, UploadItem } from '@/types'
import { computedAsync } from '@vueuse/core'
import localforageLib from 'localforage'
import { defineStore } from 'pinia'
import { computed, type Ref, ref, watch } from 'vue'

export const useSubmissionStore = defineStore('submission', () => {
	const planStore = usePlanStore()
	const services: SubmissionService[] = [
		nglService,
		sepioService,
		everplansService,
		inmanService,
		emailReceiptService,
	]
	const storeNames = services.map((service) => service.storeName)
	const serviceNames: SubmissionChannel[] = services.map(
		(service) => service.serviceName
	)
	const submitting = ref(false)

	const remoteSubmissionErrors = ref<Record<string, RemoteSubmissionStatus>>({})

	const normalizePlanId = (planId: string) => planId.toUpperCase()

	/**
	 * Fetches the current remote submission errors for the AFP
	 * and stores them for use throughout the application.
	 */
	const fetchRemoteSubmissionErrors = async (
		salesforceContactAfpId: string
	) => {
		const statuses = await useSubmissionStatusService().getSubmissionErrors(
			salesforceContactAfpId
		)

		remoteSubmissionErrors.value = Object.fromEntries(
			Object.entries(statuses).map(([planHolderId, status]) => [
				normalizePlanId(planHolderId),
				status,
			])
		)
	}

	const failedRequests: Ref<
		Record<
			SubmissionChannel,
			{
				planHolderId: string
				status: UploadItem<any>['uploadStatus']
			}[]
		>
	> = ref({
		NGL: [],
		Sepio: [],
		Inman: [],
		Everplans: [],
		Email: [],
	})

	// Value to prevent planHolderWithFailures being computed till retryAll is complete
	const isRetrying = ref(false)

	// This method will return the submissionStatuses for each service for the planHolder.
	const planHoldersWithFailures = computed(() => {
		const planHolderIds = new Set<string>()
		Object.entries(failedRequests.value).forEach(([service, requests]) => {
			requests.forEach((request) => {
				planHolderIds.add(request.planHolderId)
			})
		})

		return planHolderIds
	})

	/**
	 * Returns the total number of submission errors for a plan holder,
	 * including failed outgoing requests and remote submission errors.
	 */
	const getSubmissionErrorCount = (planHolderId: string): number => {
		const normalizedPlanHolderId = planHolderId.toUpperCase()

		const localErrorCount = (
			failedSubmissions.value?.get(planHolderId) ??
			failedSubmissions.value?.get(normalizedPlanHolderId) ??
			[]
		).filter(({ status }) =>
			['sending', 'failed', 'waitingForNetwork'].includes(status)
		).length

		const remoteStatuses =
			remoteSubmissionErrors.value[normalizedPlanHolderId]?.statuses

		// The remote endpoint returns all submission statuses for a plan when at
		// least one has an error. Each submission type has different error states,
		// so use its mapper to determine which returned statuses count as errors.
		const remoteErrorCount = remoteStatuses
			? [
					remoteStatuses.insurance &&
						new InsuranceStatusMapper(remoteStatuses.insurance),
					remoteStatuses.everplans &&
						new EverplansStatusMapper(
							remoteStatuses.everplans as EverplansSubmissionStatus
						),
					remoteStatuses.oap &&
						new OapStatusMapper(remoteStatuses.oap as OapSubmissionStatus),
				].filter((submission) => submission && isSubmissionError(submission))
					.length
			: 0

		return localErrorCount + remoteErrorCount
	}

	/**
	 * Resolves a plan holder's basic data from local storage, falling back to the
	 * CloudSync copy of the plan.
	 *
	 * Submission errors can reference plans that were never stored on this device
	 * (for example, submitted from another device, or cleared since), so a plan
	 * missing locally is not enough of a reason to drop its errors.
	 */
	const loadPlanHolderBasicData = async (
		planHolderId: string
	): Promise<Partial<PlanBasicData> | undefined> => {
		try {
			return await planStore.loadPlanBasicData(planHolderId)
		} catch {
			return await useCloudSyncService().plan.getBasicDataById(planHolderId)
		}
	}

	// This value is used by SubmissionErrors to determine which planHolder is considered to have failed requests.
	const failedSubmissions = computedAsync(async () => {
		const newFailedSubmissions = new Map<
			string,
			{
				submissionChannel: SubmissionChannel
				status: UploadItem<any>['uploadStatus']
			}[]
		>()

		const planHolderIds = [
			...planHoldersWithFailures.value,
			...Object.keys(remoteSubmissionErrors.value),
		].filter(
			(planHolderId, index, ids) =>
				ids.findIndex(
					(id) => id.toUpperCase() === planHolderId.toUpperCase()
				) === index
		)

		for (const planHolderId of planHolderIds) {
			// Skip only when the plan holder is unknown both locally and in CloudSync
			if (!(await loadPlanHolderBasicData(planHolderId))) continue

			const failedStatuses = await getSubmissionStatusById(planHolderId)
			newFailedSubmissions.set(planHolderId, failedStatuses)
		}

		return newFailedSubmissions
	})

	// This method is used when submitting initially, or retrying to submit the packet in SubmissionErrorCard
	// It will mark the packetStatus as sending, submit all requestedServices in parallel
	// After all submission requests are resolved, it will mark the packetStatus as submitted
	const submitPlanPacket = async (
		planHolder: Plan,
		requestedServices: SubmissionChannel[] = serviceNames
	) => {
		const planDataToStore = {
			packetStatus: PlanPacketStatus.Submitted,
			completedSteps: [...planHolder.completedSteps],
			oapPayment: { ...planHolder.oapPayment },
		}

		await planStore.updatePlan(planHolder.id, {
			packetStatus: PlanPacketStatus.Sending,
		})

		if (
			planHolder.funeralHomeId === import.meta.env.VITE_INTERNAL_FUNERAL_HOME_ID
		) {
			// per PSS-1509 request, if a submission is sent to the internal demo funeral home, do not submit to any service other than Everplans
			await Promise.all([
				everplansService.submitRequest(planHolder),
				emailReceiptService.submitRequest(planHolder),
			])
		} else {
			await Promise.all(
				services
					.filter(
						(service) =>
							requestedServices.includes(service.serviceName) &&
							service.shouldSubmit(planHolder)
					)
					.map((submissionService) =>
						submissionService.submitRequest(planHolder)
					)
			)
		}

		submitting.value = false
		await planStore.updatePlan(planHolder.id, planDataToStore)
		await useCloudSyncService().flushPendingUpserts()
	}

	/**
	 * This method will retry every failed submission of a particular plan holder, called by 'Retry' on submissionErrorCard
	 * If no channels are passed it runs for all channels.
	 */
	const retryByPlanHolderId = async (
		planHolderId: string,
		channels: SubmissionChannel[] | null = null
	) => {
		await Promise.all(
			services
				.filter(
					(service) =>
						channels === null || channels.includes(service.serviceName)
				)
				.map((submissionService) =>
					submissionService.retryRequestById(planHolderId)
				)
		)

		await refreshFailedRequests()
	}

	// This method will retry all failed requests from every service.
	// it will then retrieve the failed requests from each submissionService. Currently called on home dashboard sync
	const retryAllRequests = async () => {
		isRetrying.value = true

		for (const submissionService of services) {
			await submissionService.retryAllRequests()

			failedRequests.value[submissionService.serviceName as SubmissionChannel] =
				await submissionService.getFailedRequests()
		}

		isRetrying.value = false
	}

	// Refresh failed requests
	const refreshFailedRequests = async () => {
		for (const submissionService of services) {
			failedRequests.value[submissionService.serviceName as SubmissionChannel] =
				await submissionService.getFailedRequests()
		}
	}

	// This helper gathers the uploadStatus of all services given a planHolderId
	const getSubmissionStatusById = async (
		planHolderId: string
	): Promise<
		{
			submissionChannel: SubmissionChannel
			status: UploadItem<any>['uploadStatus']
		}[]
	> => {
		let planHolderSubmissionStatus: {
			submissionChannel: SubmissionChannel
			status: UploadItem<any>['uploadStatus']
		}[] = []

		for (const submissionService of services) {
			const requestItem =
				await submissionService.getSubmissionRequestById(planHolderId)
			if (requestItem) {
				planHolderSubmissionStatus.push({
					submissionChannel: submissionService.serviceName as SubmissionChannel,
					status: requestItem.uploadStatus,
				})
			}
		}

		return planHolderSubmissionStatus
	}

	// This helper returns a planHolder's merged document, used by error card in case AFP wish to download the packet
	const getPlanHolderSubmissionDocument = async (
		planHolderId: string
	): Promise<File | null> => {
		const planHolderUploadItem =
			await nglService.getSubmissionRequestById(planHolderId)
		if (planHolderUploadItem) {
			return planHolderUploadItem.requestConfig.data.document
		}
		return null
	}

	/**
	 * Syncs remote submission errors with the latest submission statuses.
	 * Adds or updates statuses that contain errors and removes them once resolved.
	 */
	const syncRemoteSubmissionErrors = (
		statuses: Record<string, SubmissionStatusGroup>
	) => {
		for (const [planHolderId, status] of Object.entries(statuses)) {
			const normalizedPlanHolderId = normalizePlanId(planHolderId)
			const existingError = remoteSubmissionErrors.value[normalizedPlanHolderId]

			if (!existingError) {
				continue
			}

			remoteSubmissionErrors.value[normalizedPlanHolderId] = {
				latestSessionId: existingError.latestSessionId,
				statuses: status,
			}

			if (getSubmissionErrorCount(normalizedPlanHolderId) === 0) {
				delete remoteSubmissionErrors.value[normalizedPlanHolderId]
			}
		}
	}

	// This method removes the planHolder's request from local forage of every service
	// Used when plan is marked 'Manually Resolved' or retry is successful
	const removeSubmissionByPlanHolderId = async (planHolderId: string) => {
		for (const submissionService of services) {
			await submissionService.removeRequestById(planHolderId)

			failedRequests.value[submissionService.serviceName as SubmissionChannel] =
				failedRequests.value[
					submissionService.serviceName as SubmissionChannel
				].filter((request) => request.planHolderId !== planHolderId)
		}

		delete remoteSubmissionErrors.value[planHolderId.toUpperCase()]
	}

	// This watcher prunes each service's store of completely successful plan submissions whenever planHolderWithFailures value is updated
	watch(planHoldersWithFailures, async (newFailedPlansList) => {
		if (isRetrying.value) return
		for (const submissionService of services) {
			const allPlansInQueue = await submissionService.getAllPlanIdInQueue()
			allPlansInQueue.forEach((planHolderId) => {
				if (!newFailedPlansList.has(planHolderId)) {
					submissionService.removeRequestById(planHolderId)
				}
			})
		}
	})

	const clearAllRequestQueueStores = async () => {
		try {
			for (const storeName of storeNames) {
				const store = localforageLib.createInstance({
					name: 'request-queue',
					storeName: storeName,
				})
				await store.clear()
			}
		} catch (error) {
			console.log('Error clearing all request-queue stores:', error)
		}
	}

	const validPaymentDetailsForSubmission = (
		planHolder: Plan,
		requestedServices: SubmissionChannel[] = serviceNames
	): SubmissionChannel[] => {
		const invalidServices: SubmissionChannel[] = []
		services
			.filter((service) => requestedServices.includes(service.serviceName))
			.forEach((service) => {
				if (!service.validateRequest(planHolder)) {
					invalidServices.push(service.serviceName)
				}
			})

		return invalidServices
	}

	return {
		clearAllRequestQueueStores,
		failedSubmissions,
		fetchRemoteSubmissionErrors,
		getPlanHolderSubmissionDocument,
		getSubmissionErrorCount,
		getSubmissionStatusById,
		loadPlanHolderBasicData,
		planHoldersWithFailures,
		refreshFailedRequests,
		remoteSubmissionErrors,
		removeSubmissionByPlanHolderId,
		retryAllRequests,
		retryByPlanHolderId,
		storeNames,
		submitPlanPacket,
		syncRemoteSubmissionErrors,
		validPaymentDetailsForSubmission,
	}
})
